
import { useState, useEffect, useCallback, useRef } from "react"
import { WifiOff, RefreshCw } from "lucide-react"
import { cn } from "@/lib/utils"
import { api } from "@/lib/api"

export function ConnectionStatus() {
  const [isOnline, setIsOnline] = useState(true)
  const [wasOffline, setWasOffline] = useState(false)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [backendDown, setBackendDown] = useState(false)
  const reconnectingRef = useRef(false)

  const tryReconnect = useCallback(async () => {
    if (reconnectingRef.current) return
    reconnectingRef.current = true
    setIsReconnecting(true)

    try {
      await api.get('/health')
      setIsOnline(true)
      setWasOffline(false)
      setBackendDown(false)
      setIsReconnecting(false)
    } catch {
      setBackendDown(true)
      setIsReconnecting(false)
    }
    reconnectingRef.current = false
  }, [])

  const handleOnline = useCallback(() => {
    setIsOnline(true)
    if (wasOffline) {
      tryReconnect()
    }
  }, [wasOffline, tryReconnect])

  const handleOffline = useCallback(() => {
    setIsOnline(false)
    setWasOffline(true)
    setBackendDown(false)
  }, [])

  useEffect(() => {
    setIsOnline(navigator.onLine)

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [handleOnline, handleOffline])

  // Detect when tab becomes visible after being hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !navigator.onLine) {
        setIsOnline(false)
        setWasOffline(true)
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  if (isOnline && !isReconnecting && !backendDown) {
    return null
  }

  return (
    <div
      className={cn(
        "fixed bottom-4 left-1/2 -translate-x-1/2 z-50",
        "flex items-center gap-2 px-4 py-2 rounded-full",
        "bg-destructive text-destructive-foreground",
        "shadow-lg animate-in fade-in slide-in-from-bottom-4",
        (isReconnecting || backendDown) && "bg-primary"
      )}
    >
      {isReconnecting ? (
        <>
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm font-medium">Reconnecting...</span>
        </>
      ) : backendDown ? (
        <>
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">Backend unavailable — retrying...</span>
          <button
            onClick={tryReconnect}
            className="ml-2 text-sm underline font-medium"
          >
            Retry now
          </button>
        </>
      ) : (
        <>
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">No internet connection</span>
        </>
      )}
    </div>
  )
}
